import React, { useState, useMemo } from 'react';
import { TabContent, TabPane, Nav, NavItem, NavLink, Button } from 'src/components/ui';

import { canExpand, ADDITIONAL_PROPERTY_FLAG } from '@rjsf/utils';
import styled from '@emotion/styled';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { interpolate } from '../../../util/mapObject';
import Jnx, { parseJnxExpr } from '../../../util/jnx';
import { useSideChannelSubscription } from '../../../util/useSideChannel';
import getPathFromId from '../../../util/getPathFromId';

import classnames from 'src/util/classnames';
import Icon from 'src/components/Icon';

function PropertiesTable({ properties, uiSchema, onAddClick, readonly }) {
  const { 'ui:keyName': keyName = '', 'ui:layout': layout = 'horizontal', ':tableClassNames': tableClassNames, 'ui:valueName': valueName = '' } =
    uiSchema || {};
  const isVertical = layout === 'vertical';
  const hasHeader = keyName || valueName;

  return (
    <PropertyTable className={`property-table ${tableClassNames || ''}`}>
      {hasHeader && properties.length ? (
        <thead>
          <tr>
            <th>{keyName}</th>
            <th>{valueName}</th>
          </tr>
        </thead>
      ) : null}
      <tbody>
        {properties.map(({ content, name }, idx) => {
          // `content` is RJSF's ObjectFieldProperty: it owns the property's path, change and rename/remove wiring.
          const { required, schema, uiSchema: propertyUiSchema, fieldPathId, handleKeyRename, handleRemoveProperty } = content.props;
          const { [ADDITIONAL_PROPERTY_FLAG]: isAdditionalProperty, title, ...newSchema } = schema;
          newSchema.title = ' ';
          const field = <td>{React.cloneElement(content, { schema: newSchema, uiSchema: { ...propertyUiSchema, 'ui:hideLabel': true } })}</td>;

          return (
            <React.Fragment key={idx}>
              <tr key={idx}>
                <td>
                  {!readonly && isAdditionalProperty ? <LabelInput label={name} required={required} id={`${fieldPathId.$id}_${name}-key`} onChange={newKey => handleKeyRename(name, newKey)} /> : name}
                </td>
                {isVertical ? null : field}
                {!readonly && isAdditionalProperty ? (
                  <td>
                    <Button
                      color="clear"
                      aria-label="Remove"
                      className="array-item-remove text-danger"
                      tabIndex="-1"
                      style={btnStyle}
                      disabled={readonly}
                      onClick={() => handleRemoveProperty(name)}
                    >
                      <Icon icon="X" weight="regular" />
                    </Button>
                  </td>
                ) : null}
              </tr>
              {isVertical ? <tr>{field}</tr> : null}
            </React.Fragment>
          );
        })}
        {!readonly && onAddClick ? (
          <tr>
            <td colSpan="2" style={{ paddingTop: 6 }}>
              <Button size="sm" color="info" className="array-item-add" onClick={onAddClick} disabled={readonly}>
                <Icon icon="Plus" weight="regular" /> {uiSchema['ui:addButtonText'] ? ` ${uiSchema['ui:addButtonText']}` : <Trans>Add</Trans>}
              </Button>
            </td>
          </tr>
        ) : null}
      </tbody>
    </PropertyTable>
  );
}

function LabelInput({ id, label, required, onChange }) {
  return (
    <input className="form-control" type="text" required={required} id={id} onBlur={event => onChange(event.target.value)} defaultValue={label} />
  );
}

const btnStyle = { flex: '0 0 32px', paddingLeft: 6, paddingRight: 6, fontWeight: 'bold' };

const PropertyTable = styled.table`
  border-spacing: 0;
  background-color: transparent;
  > tbody > tr > td {
    padding: 0;
    &:first-of-type > input.form-control.label-input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    &:nth-of-type(2) > div.form-group > div.form-group input.form-control {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    > div.form-group {
      margin: 0;
      > div.form-group {
        margin: 0;
      }
    }
  }
`;

export default PropertiesTable;
